import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
const FloatingCTA = lazy(() => import("@/components/FloatingCTA"));
import ScrollToTop from "@/components/ScrollToTop";

// Lazy-loaded routes for code splitting
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FitnessLanding = lazy(() => import("./pages/industries/FitnessLanding"));
const LawyerLanding = lazy(() => import("./pages/industries/LawyerLanding"));
const BeautyLanding = lazy(() => import("./pages/industries/BeautyLanding"));
const AccountantLanding = lazy(() => import("./pages/industries/AccountantLanding"));
const DesignerLanding = lazy(() => import("./pages/industries/DesignerLanding"));
const PhotographerLanding = lazy(() => import("./pages/industries/PhotographerLanding"));
const WebsitesService = lazy(() => import("./pages/services/WebsitesService"));
const EcommerceService = lazy(() => import("./pages/services/EcommerceService"));
const ShopifyService = lazy(() => import("./pages/services/ShopifyService"));
const CustomInterfacesService = lazy(() => import("./pages/services/CustomInterfacesService"));
const AutomationsService = lazy(() => import("./pages/services/AutomationsService"));
const AutomationSalesPage = lazy(() => import("./pages/AutomationSalesPage"));

const queryClient = new QueryClient();

const STANDALONE_ROUTES = ["/ecom-autopilot"];

const GlobalFloatingButtons = () => {
  const { pathname } = useLocation();
  if (STANDALONE_ROUTES.includes(pathname)) return null;
  return (
    <>
      <WhatsAppButton />
      <FloatingCTA />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/thank-you" element={<ThankYou />} />
            
            {/* Service Pages */}
            <Route path="/services/websites" element={<WebsitesService />} />
            <Route path="/services/ecommerce" element={<EcommerceService />} />
            <Route path="/services/shopify" element={<ShopifyService />} />
            <Route path="/services/custom-interfaces" element={<CustomInterfacesService />} />
            <Route path="/services/automations" element={<AutomationsService />} />
            
            {/* Standalone Sales Pages */}
            <Route path="/ecom-autopilot" element={<AutomationSalesPage />} />
            
            {/* Industry Landing Pages */}
            <Route path="/industries/fitness" element={<FitnessLanding />} />
            <Route path="/industries/lawyer" element={<LawyerLanding />} />
            <Route path="/industries/beauty" element={<BeautyLanding />} />
            <Route path="/industries/accountant" element={<AccountantLanding />} />
            <Route path="/industries/designer" element={<DesignerLanding />} />
            <Route path="/industries/photographer" element={<PhotographerLanding />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        
        <GlobalFloatingButtons />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;